import {
  Button,
  Feedback,
  InfoModal,
  Snackbar,
  Form,
  SubStepButtons,
} from "@talladega/components";
import { DownloadIcon } from "@talladega/icons";
import api from "@modules/api/api";
import { SubmitHandler, useForm } from "react-hook-form";
import { useRouter } from "next/router";
import { Trans, useTranslation } from "next-i18next";
import { useEffect, useState, MouseEvent, useRef } from "react";
import { Event as HotjarEvent, hotjar } from "@modules/core/hotjar/types";
import { gtmPushData } from "@talladega/helpers";
import Link from "next/link";
import customerValidation from "@common/helpers/validations/customerValidationDeprecated";
import useEligibleProvinces, {
  provinceFilters,
} from "@modules/purchase/hooks/useEligibleProvinces";
import WarrantyCoverageBanner from "@common/components/banners/WarrantyCoverageBanner";
import { useNumberMask } from "@common/hooks/useMask";
import useEmailPrivateBillOfSaleMutation from "../hooks/useEmailPrivateBillOfSaleMutation";
import {
  CombinedFormInformation,
  TUserType,
  UserType,
} from "../hooks/useBillOfSaleFormStepsReducer";
import { BuyerSellerFieldsInput } from "../types/BillOfSaleFormFields";
import {
  BillOfSalePersonalInfo,
  PersonalInfoType,
} from "../types/BillOfSalePersonalInfo";
import useSendWarrantyReferralEmailMutation from "../hooks/useSendWarrantyReferralEmailMutation";

interface FormInput {
  email: string;
}

type Props = {
  onBack: () => void;
  privateBillOfSaleId: string | null;
  formInformation: CombinedFormInformation;
  userType: TUserType;
  onGenerateBillOfSaleClick?: () => void;
  onBillOfSaleGenerated?: () => void;
};

const isValidBuyerSellerInformation = (data: BuyerSellerFieldsInput) => {
  return (
    data.firstName &&
    data.lastName &&
    data.streetAddress &&
    data.city &&
    data.postalCode &&
    data.province
  );
};

const isValidFormData = (formInfo: CombinedFormInformation) => {
  if (isValidBuyerSellerInformation(formInfo.sellerInformation)) return true;
  if (isValidBuyerSellerInformation(formInfo.buyerInformation)) return true;
  return false;
};

const generatePdfDocument = async (
  privateBillOfSaleId: string | null,
  buyerInfo: BillOfSalePersonalInfo,
  sellerInfo: BillOfSalePersonalInfo,
  userPersonalInfoType: TUserType,
  locale: string
) => {
  try {
    const response = await api.rawPost(
      `api/private-bill-of-sales/create-document/${privateBillOfSaleId}?lang=${locale}`,
      {
        json: {
          buyerInformation: {
            ...buyerInfo,
            personalInfoTypeId: PersonalInfoType.Buyer,
          },
          sellerInformation: {
            ...sellerInfo,
            personalInfoTypeId: PersonalInfoType.Seller,
          },
          userPersonalInfoTypeId:
            userPersonalInfoType === UserType.Seller
              ? PersonalInfoType.Seller
              : PersonalInfoType.Buyer,
        },
      }
    );
    const fileBlob = await response.blob();
    const downloadedFile = new File([fileBlob], `bos_document.pdf`, {
      type: fileBlob.type,
    });
    const previewUrl = URL.createObjectURL(downloadedFile);
    return previewUrl;
  } catch {
    return "";
  }
};

const DownloadBillOfSaleStep = ({
  onBack,
  privateBillOfSaleId,
  formInformation,
  userType,
  onGenerateBillOfSaleClick,
  onBillOfSaleGenerated,
}: Props) => {
  const { t } = useTranslation(["common", "billOfSalePage"]);
  const router = useRouter();
  const locale = router.locale || "en";
  const source = router.query?.source as string;
  const { unMaskPipe: unMaskNumberPipe } = useNumberMask();

  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const {
    register,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<FormInput>();

  const { provinces: eligibleProvinces } = useEligibleProvinces(
    source || undefined,
    provinceFilters.isAftermarketEnabled
  );

  const emailPrivateBillOfSaleMutation = useEmailPrivateBillOfSaleMutation();
  const useSendReferral = useSendWarrantyReferralEmailMutation();

  const { vehicleDetails, buyerInformation, sellerInformation } =
    formInformation;

  const buyerInfo: BillOfSalePersonalInfo = {
    ...buyerInformation,
    province: buyerInformation.province?.abbr ?? "",
    personalInfoTypeId: PersonalInfoType.Buyer,
  };

  const sellerInfo: BillOfSalePersonalInfo = {
    ...sellerInformation,
    province: sellerInformation.province?.abbr ?? "",
    personalInfoTypeId: PersonalInfoType.Seller,
  };

  const [legalTermsChecked, setLegalTermsChecked] = useState<boolean>(false);
  const [marketingConsentChecked, setMarketingConsentChecked] =
    useState<boolean>(false);
  const [legalTermsCheckboxError, setLegalTermsCheckboxError] =
    useState<string>();
  const checkBoxRef = useRef<HTMLInputElement>(null);

  const sendReferralEmail = () => {
    if (
      privateBillOfSaleId == null ||
      (userType && userType !== UserType.Seller)
    )
      return;

    // Use seller's province as the referee province if buyer's province is not available.
    const refereeProvinceAbbr =
      formInformation.buyerInformation.province?.abbr ||
      formInformation.sellerInformation.province?.abbr ||
      "";

    const isRefereeProvinceEligible = eligibleProvinces.find(
      (p) => p.abbr === refereeProvinceAbbr
    );

    if (isRefereeProvinceEligible) {
      useSendReferral.mutate({
        id: privateBillOfSaleId,
        locale,
        refereeProvinceAbbr,
        hasGivenMarketingConsent: marketingConsentChecked,
      });
    }
  };

  const handleCreateViewPdfClick = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!isValidFormData(formInformation)) {
      setIsConfirmModalOpen(true);
      return;
    }

    if (!legalTermsChecked) {
      setLegalTermsCheckboxError(
        t("billOfSalePage:acknowledging_terms_conditions")
      );
      checkBoxRef.current?.focus();
      return;
    }

    sendReferralEmail();
    onGenerateBillOfSaleClick?.();

    const pdfUrl = await generatePdfDocument(
      privateBillOfSaleId,
      buyerInfo,
      sellerInfo,
      userType,
      router.locale ?? "en"
    );

    onBillOfSaleGenerated?.();

    if (pdfUrl === "") {
      return;
    }
    window.open(pdfUrl, "_blank");

    gtmPushData({
      event: "BOS-Form-Activity",
      element: "Download Bill of Sale",
      description: "Private Bill of Sale Download",
      bosVin: vehicleDetails.vin || "",
      bosId: privateBillOfSaleId || "",
    });
    hotjar.event(HotjarEvent.PrivateBillOfSaleDownload);
  };

  const handleSendToEmail: SubmitHandler<FormInput> = async (
    form: FormInput
  ) => {
    if (!privateBillOfSaleId) return;
    if (!isValidFormData(formInformation)) {
      setIsConfirmModalOpen(true);
      return;
    }

    if (!legalTermsChecked) {
      setLegalTermsCheckboxError(t("billOfSalePage:please_acknowledge"));
      checkBoxRef.current?.focus();
      return;
    }

    sendReferralEmail();

    try {
      await emailPrivateBillOfSaleMutation.mutateAsync({
        id: privateBillOfSaleId,
        email: form.email,
        buyerInformation: buyerInfo,
        sellerInformation: sellerInfo,
        userPersonalInfoTypeId:
          userType === UserType.Seller
            ? PersonalInfoType.Seller
            : PersonalInfoType.Buyer,
        locale: router.locale ?? "en",
      });

      gtmPushData({
        event: "BOS-Form-Activity",
        element: "Download Bill of Sale",
        description: "Private Bill of Sale Email Sent",
        bosVin: vehicleDetails.vin || "",
        bosId: privateBillOfSaleId || "",
      });

      hotjar.event(HotjarEvent.PrivateBillOfSaleSendEmail);
      Snackbar.Pop({
        message: t("billOfSalePage:bill_of_sale_sent_successfully"),
      });
    } catch {
      Snackbar.Pop({
        message: t("common:something_went_wrong"),
      });
    }
  };

  useEffect(() => {
    if (!isValidFormData(formInformation)) {
      setIsConfirmModalOpen(true);
    }
  }, [formInformation]);

  return (
    <div className="flex flex-col gap-8">
      <div className="mt-6 p-8 w-full border rounded-xl border-gray-200">
        <p className="caption-1 pb-4 text-gray">
          {t("billOfSalePage:bill_of_sale_ready")}
        </p>
        <p className="caption-3 text-gray-500">
          {t("billOfSalePage:trouble_accessing_pdf")}
        </p>

        <Form.Checkbox
          id="legalTermsCheckbox"
          className="mt-4"
          label={
            <Trans
              t={t}
              i18nKey="billOfSalePage:general_terms_and_conditions"
              components={{
                a: (
                  <Link
                    className="text-primary-bold underline"
                    href="/privacy-policy"
                    target="_blank"
                  />
                ),
                b: (
                  <Link
                    className="text-primary-bold underline"
                    href="/terms-conditions"
                    target="_blank"
                  />
                ),
              }}
            />
          }
          ref={checkBoxRef}
          onChange={(e) => {
            setLegalTermsChecked(e.target.checked);
            if (e.target.checked) setLegalTermsCheckboxError("");
          }}
        />
        {legalTermsCheckboxError && (
          <div className="mt-2">
            <Feedback status="error">{legalTermsCheckboxError}</Feedback>
          </div>
        )}

        <Form.Checkbox
          id="marketingConsentCheckbox"
          className="mt-4"
          label={t("billOfSalePage:general_form_agreement")}
          onChange={(e) => {
            setMarketingConsentChecked(e.target.checked);
          }}
        />
        <div className="py-6 flex flex-col-reverse sm:flex-row gap-4">
          <SubStepButtons
            hideNextButton
            backButton={{
              disabled: isSubmitting,
              onClick: onBack,
              className: "w-full sm:w-36",
            }}
          />
          <Button
            className="w-full sm:w-fit"
            spacing="hug"
            leftIcon={<DownloadIcon className="h-5 w-5" />}
            onClick={handleCreateViewPdfClick}
          >
            {t("billOfSalePage:create_and_view_pdf")}
          </Button>
        </div>
        <p className="caption-1 text-gray">
          {t("billOfSalePage:provide_email_address")}
        </p>
        <div className="py-4">
          <Form.Input
            id="email"
            label={t("common:email")}
            type="email"
            autoComplete="email"
            maxLength={254}
            defaultValue={formInformation?.sourceInformation.email || ""}
            feedback={errors.email?.message}
            status={errors.email ? "error" : "default"}
            {...register("email", {
              required: {
                value: true,
                message: t("common:required"),
              },
              maxLength: 254,
              pattern: {
                value: customerValidation.email.pattern,
                message: t("common:invalid_email"),
              },
            })}
          />
        </div>
        <Button
          className="w-full sm:w-fit"
          spacing="hug"
          fill="outline"
          onClick={handleSubmit(handleSendToEmail)}
          disabled={isSubmitting}
        >
          {t("billOfSalePage:send_my_bill_of_sale")}
        </Button>
        <InfoModal
          title={t("billOfSalePage:missing_information")}
          isOpen={isConfirmModalOpen}
          onClose={() => setIsConfirmModalOpen(false)}
          primaryButtonText={t("billOfSalePage:ok_great_thanks")}
        >
          <div className="lg:w-[27.25rem] mx-auto">
            <h5 className="text-black">
              {t("billOfSalePage:to_generate_bos_we_need")}
            </h5>
            <p className="text-base mt-4 text-black">
              {t("billOfSalePage:please_go_back_earlier_steps")}
            </p>
          </div>
        </InfoModal>
      </div>
      <div>
        {userType && userType === UserType.Buyer && (
          <WarrantyCoverageBanner
            buyerInformation={{
              first: formInformation.buyerInformation.firstName ?? "",
              last: formInformation.buyerInformation.lastName ?? "",
              email: formInformation.buyerInformation.email,
              province: formInformation.buyerInformation.province?.abbr ?? "",
            }}
            vehicleDetails={{
              vin: formInformation.vehicleDetails.vin,
              year: formInformation.vehicleDetails.year?.toString() ?? "",
              make: formInformation.vehicleDetails.make,
              model: formInformation.vehicleDetails.model,
              odometer: unMaskNumberPipe(
                formInformation.vehicleDetails.odometer ?? ""
              ).toString(),
            }}
          />
        )}
      </div>
    </div>
  );
};

export default DownloadBillOfSaleStep;
